import {
  decodeActionCard,
  getInformationalCardKind,
  isTodoActionCard,
  type ActionCardSurface,
  type DeferredDismissState,
} from '@littlebird/action-cards';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import {
  CardV2,
  FeedCard,
  hasViewFor,
  openCardChatTab,
} from '@/features/action-cards-view';
import { actionCardAnalyticsSnapshot } from '@/features/action-cards/analytics';
import {
  feedCardIcon,
  feedCardProvider,
  feedCheckIcon,
} from '@/features/home/components/feedCardIcon';
import { useTabs } from '@/features/tabs';

import type { CardV2ItemLayout } from './CardV2Item';
import type { ActionCard, ActionCardDismissal } from '@littlebird/sdk';

type ChatFeedCardProps = {
  card: ActionCard;
  reportedSurface: ActionCardSurface;
  deferredDismiss: DeferredDismissState;
  // A page that shows the card's chat beside it opens that chat instead of a new tab
  openInCardChat?: (seed?: string) => void;
  // A page that lists the card dismisses it there, with its own Undo
  onDismiss?: (dismissal?: ActionCardDismissal) => void;
  layout: CardV2ItemLayout;
};

const isActionable = (card: ActionCard) =>
  card.status === 'draft' ||
  card.status === 'running' ||
  card.status === 'failed';

// A card in a chat or a list, drawn as the Feed draws it: open while it can be acted on, its row once settled.
// A dismissed card keeps the chat's own fold, which offers Restore in place
export const ChatFeedCard = observer(function ChatFeedCard({
  card,
  reportedSurface,
  deferredDismiss,
  openInCardChat,
  onDismiss,
  layout,
}: ChatFeedCardProps) {
  // Views arrive by sync on the same MobX card, so React Compiler must not cache this
  'use no memo';
  const { openTab } = useTabs();
  const [open, setOpen] = useState(() => isActionable(card));
  const decoded = decodeActionCard(card);
  // Every control on the card dismisses through this, so the host's dismissal covers them all
  const dismissal: DeferredDismissState = onDismiss
    ? {
        ...deferredDismiss,
        beginDismiss: (_targets, reason) => onDismiss(reason),
      }
    : deferredDismiss;

  if (!decoded || !hasViewFor(card, 'web_feeds') || card.status === 'dismissed')
    return (
      <CardV2
        card={card}
        surface="web_chat"
        deferredDismiss={deferredDismiss}
        reportedSurface={reportedSurface}
      />
    );

  const feedCard = (
    <FeedCard
      card={card}
      surface="web_feeds"
      reportedSurface={reportedSurface}
      inChat={reportedSurface === 'chat'}
      icon={
        layout === 'list'
          ? feedCheckIcon({ card, decoded })
          : feedCardIcon({ card, decoded })
      }
      todo={isTodoActionCard(decoded.content)}
      appName={feedCardProvider({ decoded }).label}
      open={open}
      onToggle={() => setOpen(!open)}
      deferredDismiss={dismissal}
      onDismiss={() =>
        dismissal.beginDismiss([
          {
            cardId: card.id,
            actionType: card.actionType,
            cardKind: getInformationalCardKind(card),
            origin: card.origin,
            surface: reportedSurface,
            analytics: actionCardAnalyticsSnapshot(card, reportedSurface, {
              card_expanded: open,
            }),
          },
        ])
      }
      onRetain={() => {}}
      onOpenCardChat={
        openInCardChat
          ? () => openInCardChat()
          : () =>
              card.followUpChatId
                ? openTab({ path: `/chats/${card.followUpChatId}` })
                : openCardChatTab(card, openTab)
      }
      onOpenInCardChat={openInCardChat}
    />
  );

  // A chat card stands alone under its message, so one outer card holds its row and its open card (feed.css)
  return layout === 'list' ? (
    feedCard
  ) : (
    <div data-feed-chat-card="">{feedCard}</div>
  );
});
